/**
 * AdminViewContext v1.0 (T779, 2026-10-14) — the one "Admin view" switch.
 *
 * Who is an admin is decided on the server only (public.user_roles, read by
 * caller_is_superadmin() through useSuperadmin). Never a URL parameter, never a
 * column the user can write.
 *
 * Whether an admin is LOOKING at admin things is a separate choice, made only by
 * clicking the "Admin view" switch in the top header. The choice is saved to the
 * account (user_metadata.admin_view) with localStorage `fox_admin_view` as the
 * fallback, so it survives page changes, reloads and sign-ins. No route and no
 * redirect may change it: setAdminView is called from the switch and nowhere else.
 *
 * showAdmin = isAdmin && adminView. Non-admins always get false, whatever the
 * saved value says.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSuperadmin } from "@/hooks/useSuperadmin";

export const ADMIN_VIEW_STORAGE_KEY = "fox_admin_view";

interface AdminViewState {
  /** Server-side admin flag. */
  isAdmin: boolean;
  /** The saved switch position (meaningless for non-admins). */
  adminView: boolean;
  /** isAdmin && adminView — what every admin-only surface should check. */
  showAdmin: boolean;
  loading: boolean;
  setAdminView: (on: boolean) => Promise<void>;
}

const AdminViewCtx = createContext<AdminViewState>({
  isAdmin: false,
  adminView: false,
  showAdmin: false,
  loading: true,
  setAdminView: async () => {},
});

export function useAdminView() {
  return useContext(AdminViewCtx);
}

function readLocal(): boolean | null {
  try {
    const v = localStorage.getItem(ADMIN_VIEW_STORAGE_KEY);
    return v === "1" ? true : v === "0" ? false : null;
  } catch {
    return null;
  }
}

function writeLocal(on: boolean) {
  try {
    localStorage.setItem(ADMIN_VIEW_STORAGE_KEY, on ? "1" : "0");
  } catch {
    /* storage blocked: the account copy still holds the choice */
  }
}

export function AdminViewProvider({ children }: { children: React.ReactNode }) {
  const { isSuperadmin, loading: adminLoading } = useSuperadmin();
  const [adminView, setAdminViewState] = useState<boolean>(() => readLocal() ?? false);

  // The account copy wins over the browser copy; it follows the admin across
  // devices and sign-ins. Read on load and on every auth change (sign-in,
  // token refresh, user update).
  useEffect(() => {
    let alive = true;
    const apply = (meta: Record<string, unknown> | undefined) => {
      const saved = meta?.admin_view;
      if (typeof saved === "boolean" && alive) {
        setAdminViewState(saved);
        writeLocal(saved);
      }
    };
    void supabase.auth.getSession().then(({ data }) => apply(data.session?.user?.user_metadata));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_evt, session) => {
      apply(session?.user?.user_metadata);
    });
    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);

  const setAdminView = useCallback(async (on: boolean) => {
    setAdminViewState(on);
    writeLocal(on);
    const { error } = await supabase.auth.updateUser({ data: { admin_view: on } });
    if (error) console.error("[AdminView] could not save to the account; kept in this browser:", error.message);
  }, []);

  const value = useMemo<AdminViewState>(() => ({
    isAdmin: isSuperadmin,
    adminView,
    showAdmin: isSuperadmin && adminView,
    loading: adminLoading,
    setAdminView,
  }), [isSuperadmin, adminView, adminLoading, setAdminView]);

  return <AdminViewCtx.Provider value={value}>{children}</AdminViewCtx.Provider>;
}
